import { useEffect, type RefObject } from 'react';
import {
  getDialogAncestor,
  handleModalEscapeAtDocumentCapture,
} from '../utils/modalFormKeyboard';

/**
 * Document capture-phase Escape for modal forms and wizards (#1432).
 *
 * Footer CTAs and the modal close control sit outside the `<form>`; without
 * this hook Escape is a no-op on those controls. The handler also defers when
 * an inner overlay (calendar, combobox, nested dialog) is open and only runs
 * when this form's owning dialog is the topmost visible dialog.
 */
export function useModalFormEscapeClose(
  containerRef: RefObject<HTMLElement | null>,
  onRequestClose: (() => void) | undefined,
  enabled: boolean,
): void {
  useEffect(() => {
    if (!enabled || !onRequestClose) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      const escapeScope = getDialogAncestor(container) ?? container;
      handleModalEscapeAtDocumentCapture(event, escapeScope, onRequestClose);
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [enabled, onRequestClose, containerRef]);
}
